import * as React from "react";
import { useFocusTrap } from "@/hooks/use-focus-trap";

/**
 * A modal overlay that meets the DS Modal contract without changing the DOM.
 *
 * Renders exactly the `<div>` it replaces — same class names, same handlers —
 * and adds the things the contract requires and hand-rolled overlays almost
 * always miss: Tab is trapped inside, background scroll is locked, focus
 * returns to whatever opened it, and Esc closes the layer.
 *
 *   <div className="modal-overlay" onClick={onClose}>   ->
 *   <FocusScope className="modal-overlay" onClick={onClose}>
 *
 * Esc is delivered as a synthetic click on the scrim element itself, so it
 * runs the caller's existing close handler — including any
 * `e.target === e.currentTarget` guard or in-flight (`isPending`) check —
 * rather than a second close path that can drift from it. Only the topmost
 * scope reacts (a shared stack), and a scope stands down while focus is
 * outside it (a portalled Radix menu above it owns the key until it closes).
 */

const escStack: HTMLElement[] = [];

export const FocusScope = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function FocusScope(props, _forwarded) {
    const ref = useFocusTrap<HTMLDivElement>(true);

    React.useEffect(() => {
      const el = ref.current;
      if (!el) return;
      escStack.push(el);
      const onKey = (e: KeyboardEvent) => {
        if (e.key !== "Escape" || e.defaultPrevented) return;
        if (escStack[escStack.length - 1] !== el) return;
        const active = document.activeElement;
        if (active && active !== document.body && !el.contains(active)) return;
        e.stopPropagation();
        el.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
        el.click();
      };
      document.addEventListener("keydown", onKey);
      return () => {
        const i = escStack.indexOf(el);
        if (i !== -1) escStack.splice(i, 1);
        document.removeEventListener("keydown", onKey);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <div ref={ref} {...props} />;
  },
);
